import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { MachineModelHealthStatus, DeploymentTargetResource, EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { MachineModelHealthStatusValues } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { AdvancedTargetConfigurationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/AdvancedTargetConfigurationPage";
import { BasicTargetConfigurationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/BasicTargetConfigurationPage";
import type { ConfigurationProps, ConfigurationState } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialog";
import { ConfigurationDialog } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialog";
import { NfsDriverInstallationHelpContent, TargetConfigurationHelpContent, targetConfigurationHelpImage, targetNfsDriverInstallationHelpImage, TentacleInstallationHelpContent, tentacleInstallationHelpImage, } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialogHelp";
import { ConfigurationMessages } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationMessages";
import { InstallationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/InstallationPage";
import type { BaseConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { createTargetHelmCommand } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/createHelmCommand";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { repository } from "~/clientInstance";
import type { PagedOnboardingDialogPageData, PageNavigation } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
type TargetFieldErrors = {
    [K in keyof TargetConfigurationResource]?: string;
};
interface TargetConfigurationState extends ConfigurationState<TargetConfigurationResource> {
    environments: EnvironmentResource[];
    machineRoles: string[];
    foundDeploymentTarget?: DeploymentTargetResource;
}
export interface TargetConfigurationResource extends BaseConfiguration {
    EnvironmentIds: string[];
    MachineRoles: string[];
    DefaultNamespace: string;
    IncludeMonitorHelmCommands: boolean;
}
export function KubernetesAgentTargetConfigurationDialog(props: ConfigurationProps) {
    const helmChartMajorVersion = 2;
    const permissionsControllerEnabled = useOctopusFeatureToggle("octopus-permissions-controller", false);
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    return <KubernetesAgentTargetConfigurationDialogInternal {...props} helmChartMajorVersion={helmChartMajorVersion} permissionsControllerEnabled={permissionsControllerEnabled} isGrpcHaEnabled={isGrpcHaEnabled}/>;
}
export type HelmChartMajorVersion = 1 | 2;
export type AgentTargetConfigDialogProps = ConfigurationProps<TargetConfigurationResource> & {
    helmChartMajorVersion: HelmChartMajorVersion;
    permissionsControllerEnabled: boolean;
    isGrpcHaEnabled: boolean;
};
export class KubernetesAgentTargetConfigurationDialogInternal extends ConfigurationDialog<TargetConfigurationState, TargetConfigurationResource, AgentTargetConfigDialogProps> {
    constructor(props: AgentTargetConfigDialogProps) {
        super(props, EndpointSelectionScope.DeploymentTarget, ConfigurationMessages.Name.DeploymentTarget);
    }
    onDeploymentTargetFound = (target: DeploymentTargetResource) => {
        this.setState({ foundDeploymentTarget: target });
    };
    onDeploymentTargetHealthStatusUpdated = (target: DeploymentTargetResource) => {
        this.setState({ foundDeploymentTarget: target });
    };
    override initialModel() {
        return {
            ...this.getInitialBaseModel(),
            EnvironmentIds: [],
            MachineRoles: [],
            DefaultNamespace: "",
            IncludeMonitorHelmCommands: true,
        };
    }
    override initialiseState() {
        this.state = {
            ...this.initialBaseState,
            environments: [],
            machineRoles: [],
        };
    }
    override async loadMachineTypeSpecificData() {
        const environmentsPromise = repository.Environments.all();
        const machineRolesPromise = repository.MachineRoles.all();
        this.setState({
            environments: await environmentsPromise,
            machineRoles: await machineRolesPromise,
        });
    }
    override async validateMachineTypeSpecificData(model: TargetConfigurationResource, fieldErrors: TargetFieldErrors): Promise<void> {
        if (StringHelper.isNullOrWhiteSpace(model.Name)) {
            fieldErrors.Name = this.nameErrorMessages.Validation.NotProvided;
        }
        else {
            const existingMachine = await repository.Machines.list({ name: this.state.model.Name });
            if (existingMachine.TotalResults > 0) {
                fieldErrors.Name = this.nameErrorMessages.Validation.AlreadyExists;
            }
        }
        if (model.EnvironmentIds.length === 0) {
            fieldErrors.EnvironmentIds = ConfigurationMessages.EnvironmentIds.Validation.NotProvided;
        }
        if (model.MachineRoles.length === 0) {
            fieldErrors.MachineRoles = ConfigurationMessages.MachineRoles.Validation.NotProvided;
        }
    }
    override machineIsFound() {
        return !!this.state.foundDeploymentTarget;
    }
    override machineHealthStatus(): MachineModelHealthStatus | undefined {
        return this.state.foundDeploymentTarget?.HealthStatus;
    }
    override getLinkToMachine() {
        if (this.state.foundDeploymentTarget) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return links.deploymentTargetSettingsPage.generateUrl({ spaceId: repository.spaceId!, machineId: this.state.foundDeploymentTarget.Id });
        }
    }
    validateBasicFields = async (): Promise<boolean> => {
        this.clearErrors();
        const fieldErrors: TargetFieldErrors = {};
        const model = this.state.model;
        if (StringHelper.isNullOrWhiteSpace(model.Name)) {
            fieldErrors.Name = this.nameErrorMessages.Validation.NotProvided;
        }
        else {
            const existingMachine = await repository.Machines.list({ name: this.state.model.Name });
            if (existingMachine.TotalResults > 0) {
                fieldErrors.Name = this.nameErrorMessages.Validation.AlreadyExists;
            }
        }
        if (model.EnvironmentIds.length === 0) {
            fieldErrors.EnvironmentIds = ConfigurationMessages.EnvironmentIds.Validation.NotProvided;
        }
        if (model.MachineRoles.length === 0) {
            fieldErrors.MachineRoles = ConfigurationMessages.MachineRoles.Validation.NotProvided;
        }
        if (Object.keys(fieldErrors).length > 0) {
            this.setValidationErrors("The following fields have invalid values", fieldErrors);
            return false;
        }
        return true;
    };
    override getFirstConditionalPageIndex(): number {
        return 2;
    }
    override getPages() {
        const showRegenerateTokenButton = this.state.hasTokenExpired && !this.state.foundDeploymentTarget;
        const disabledBecauseHaIsNotSupported = this.state.requiresHaConfiguration && !this.props.isGrpcHaEnabled;
        let pageIndex = 1; // Set to 1 because we aren't incrementing until after the first page is added
        const pages: PagedOnboardingDialogPageData[] = [
            {
                name: "Add Kubernetes Agent",
                title: "Add New Kubernetes Agent",
                progressTrackerLabel: "Configure",
                helpPanelContent: <TargetConfigurationHelpContent />,
                helpPanelImage: targetConfigurationHelpImage(),
                isPageModelValid: () => this.validateBasicFields(),
                hideBackButton: true,
                alwaysShowCancelButton: true,
                isBusy: this.state.isBusy,
                customPrimaryButton: (pageNavigation: PageNavigation) => (<ActionButton label="Quick Setup" type={ActionButtonType.Primary} onClick={async () => {
                        if (await this.validateBasicFields()) {
                            if (!this.state.requiresHaConfiguration) {
                                await this.refreshToken();
                            }
                            const selectedEnvName = this.state.environments
                                .filter((env) => this.state.model.EnvironmentIds.includes(env.Id))
                                .map((env) => env.Name)
                                .join(";");
                            this.props.dispatchAction("Submit Kubernetes Agent Form", {
                                agentName: this.state.model.Name,
                                environments: selectedEnvName,
                            });
                            this.props.onTargetCompleted?.(this.state.model);
                            pageNavigation.moveToPageIndex(this.getFirstConditionalPageIndex());
                        }
                    }}/>),
                customSecondaryButton: (pageNavigation: PageNavigation) => (<ActionButton label="Advanced Setup" onClick={async () => {
                        if (await this.validateBasicFields()) {
                            this.setState({ advancedVisited: true });
                            await pageNavigation.moveToNextPage();
                        }
                    }} disabled={!pageNavigation.canMoveToNext}/>),
                render: () => (<BasicTargetConfigurationPage model={this.state.model} onModelChange={(model) => this.setModelState(model)} environments={this.state.environments} machineRoles={this.state.machineRoles} getFieldError={(name: string) => this.getFieldError(name)}/>),
            },
            {
                name: "Advanced Settings",
                title: "Advanced Settings",
                progressTrackerLabel: this.state.advancedVisited ? "Advanced" : undefined,
                helpPanelContent: <TargetConfigurationHelpContent />,
                helpPanelImage: targetConfigurationHelpImage(),
                isPageModelValid: () => this.validateAdvancedFields(),
                onMovingToNextPage: async () => {
                    if (!this.state.requiresHaConfiguration) {
                        await this.refreshToken();
                    }
                    const selectedEnvName = this.state.environments
                        .filter((env) => this.state.model.EnvironmentIds.includes(env.Id))
                        .map((env) => env.Name)
                        .join(";");
                    this.props.dispatchAction("Submit Kubernetes Agent Advanced Settings Form", {
                        agentName: this.state.model.Name,
                        environments: selectedEnvName,
                        storageClass: this.state.model.StorageClassName,
                    });
                    this.props.onTargetCompleted?.(this.state.model);
                },
                isBusy: this.state.isBusy,
                customSecondaryButton: this.moveToBasicOrAdvancedPageBackButton(pageIndex++),
                hideBackButton: true,
                render: () => (<AdvancedTargetConfigurationPage model={this.state.model} onModelChange={(model) => this.setModelState(model)} canModifyServerUrls={this.state.canModifyServerUrls} getFieldError={(name: string) => this.getFieldError(name)} usePersistentVolumeStorage={this.state.usePersistentVolumeStorage} onUsePersistentVolumeStorageChanged={(usePv) => this.setState({ usePersistentVolumeStorage: usePv })} useOctopusPermissionsController={this.state.useOctopusPermissionsController} onUseOctopusPermissionsControllerChanged={(useOctopusPermissionsController) => {
                        if (!useOctopusPermissionsController) {
                            this.setModelState({ DefaultClusterRolePermissions: null });
                        }
                        this.setState({ useOctopusPermissionsController });
                    }} permissionsControllerEnabled={this.props.permissionsControllerEnabled} requiresHaConfiguration={this.state.requiresHaConfiguration} isGrpcHaEnabled={this.props.isGrpcHaEnabled}/>),
            },
        ];
        if (this.state.requiresHaConfiguration) {
            pages.push(this.getHaConfigurationPage(<TargetConfigurationHelpContent />, targetConfigurationHelpImage(), this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
        }
        if (this.state.useOctopusPermissionsController) {
            pages.push(this.getOctopusPermissionsControllerPage(this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
            pages.push(this.getDefaultPermissionsPage(this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
        }
        if (!this.state.usePersistentVolumeStorage) {
            pages.push(this.getNfsCsiDriverInstallationPage(<NfsDriverInstallationHelpContent machineType={EndpointSelectionScope.DeploymentTarget}/>, targetNfsDriverInstallationHelpImage(), this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
        }
        const installationComplete = this.props.guidedSetup ? this.machineHealthStatus() === MachineModelHealthStatusValues.Healthy : undefined;
        pages.push({
            name: "Install Kubernetes Agent",
            title: "Install Kubernetes Agent",
            progressTrackerLabel: "Install",
            helpPanelContent: <TentacleInstallationHelpContent machineType={EndpointSelectionScope.DeploymentTarget}/>,
            helpPanelImage: tentacleInstallationHelpImage(),
            render: () => (<InstallationPage<DeploymentTargetResource> model={this.state.model} onAgentFound={this.onDeploymentTargetFound} onAgentHealthStatusUpdated={this.onDeploymentTargetHealthStatusUpdated} hasTokenExpired={this.state.hasTokenExpired} accessTokenExpiry={this.state.accessTokenExpiry} 
            // Kubernetes monitor is not supported in HA clusters unless grpc-high-availability is enabled
            generateHelmCommand={(selectedLanguage) => createTargetHelmCommand(selectedLanguage, this.state.model, this.state.environments, !disabledBecauseHaIsNotSupported)} fetchAgent={async (repository, name) => {
                    const targets = await repository.Machines.list({ name });
                    return targets.Items.find((t) => t.Name === name);
                }} machineType={EndpointSelectionScope.DeploymentTarget}/>),
            alwaysShowCloseButton: this.props.guidedSetup ? true : this.machineIsFound(),
            onlyShowCloseButton: installationComplete,
            customSecondaryButton: !installationComplete ? this.moveToBasicOrAdvancedPageBackButton(pageIndex++) : undefined,
            hideBackButton: true,
            customPrimaryButton: (showRegenerateTokenButton && <ActionButton label="Regenerate token" onClick={() => this.refreshToken()} type={ActionButtonType.Primary}/>) || undefined,
            onPageDisplayed: async () => {
                this.props.onEstablishingConnection?.(this.state.model);
            },
        });
        return pages;
    }
}
